// Plugin de PrimeVue para Worktime Client
// Configuración de componentes y tema Aura

import BadgeDirective from "primevue/badgedirective";
import PrimeVue from "primevue/config";
import ConfirmationService from "primevue/confirmationservice";
import DialogService from "primevue/dialogservice";
import FocusTrap from "primevue/focustrap";
import Ripple from "primevue/ripple";
import StyleClass from "primevue/styleclass";
import ToastService from "primevue/toastservice";
import Tooltip from "primevue/tooltip";
import type { App } from "vue";

// Componentes más utilizados
import Button from "primevue/button";
import Calendar from "primevue/calendar";
import Card from "primevue/card";
import Checkbox from "primevue/checkbox";
import Chip from "primevue/chip";
import Column from "primevue/column";
import ConfirmDialog from "primevue/confirmdialog";
import DataTable from "primevue/datatable";
import DatePicker from "primevue/datepicker";
import Dialog from "primevue/dialog";
import Dropdown from "primevue/dropdown";
import Editor from "primevue/editor";
import InputMask from "primevue/inputmask";
import InputText from "primevue/inputtext";
import Message from "primevue/message";
import ProgressSpinner from "primevue/progressspinner";
import Select from "primevue/select";
import Skeleton from "primevue/skeleton";
import Tag from "primevue/tag";
import Toast from "primevue/toast";

// Componentes de formularios (PrimeVue 4)
import Form from "@primevue/forms/form";
import FormField from "@primevue/forms/formfield";

// Componentes de Tabs
import Tab from "primevue/tab";
import TabList from "primevue/tablist";
import TabPanel from "primevue/tabpanel";
import TabPanels from "primevue/tabpanels";
import Tabs from "primevue/tabs";

// Tema Aura
import Aura from "@primevue/themes/aura";

export function registerPrimeVue(app: App): void {
  // Directivas
  app.directive("tooltip", Tooltip);
  app.directive("ripple", Ripple);
  app.directive("styleclass", StyleClass);
  app.directive("focustrap", FocusTrap);
  app.directive("badge", BadgeDirective);

  // Componentes principales (registro global para usar sin imports)
  app.component("Button", Button);
  app.component("Calendar", Calendar);
  app.component("Card", Card);
  app.component("Checkbox", Checkbox);
  app.component("Chip", Chip);
  app.component("Column", Column);
  app.component("ConfirmDialog", ConfirmDialog);
  app.component("DataTable", DataTable);
  app.component("DatePicker", DatePicker);
  app.component("Dialog", Dialog);
  app.component("Dropdown", Dropdown);
  app.component("Editor", Editor);
  app.component("InputMask", InputMask);
  app.component("InputText", InputText);
  app.component("Message", Message);
  app.component("ProgressSpinner", ProgressSpinner);
  app.component("Select", Select);
  app.component("Skeleton", Skeleton);
  app.component("Tag", Tag);
  app.component("Toast", Toast);

  // Componentes de formularios
  app.component("Form", Form);
  app.component("FormField", FormField);

  // Componentes de Tabs
  app.component("Tabs", Tabs);
  app.component("TabList", TabList);
  app.component("Tab", Tab);
  app.component("TabPanels", TabPanels);
  app.component("TabPanel", TabPanel);

  // Configuración de PrimeVue
  app.use(PrimeVue, {
    theme: {
      preset: Aura,
      options: {
        darkModeSelector: false,
        cssLayer: false, // Importante para Shadow DOM
      },
    },
    // Configuración de z-index para mantener jerarquía correcta con GWT
    // Dialog: 1100, Toast: 20001 (para estar siempre visible)
    zIndex: {
      modal: 1100, // Diálogos modales base
      overlay: 1000, // Overlays
      menu: 1000, // Menús desplegables
      tooltip: 1100, // Tooltips
      toast: 20001, // Toasts - mismo nivel que tenía v-snackbar en Vue 2
    },
    locale: {
      firstDayOfWeek: 1,
      dayNames: [
        "Domingo",
        "Lunes",
        "Martes",
        "Miércoles",
        "Jueves",
        "Viernes",
        "Sábado",
      ],
      dayNamesShort: ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"],
      dayNamesMin: ["D", "L", "M", "X", "J", "V", "S"],
      monthNames: [
        "Enero",
        "Febrero",
        "Marzo",
        "Abril",
        "Mayo",
        "Junio",
        "Julio",
        "Agosto",
        "Septiembre",
        "Octubre",
        "Noviembre",
        "Diciembre",
      ],
      monthNamesShort: [
        "Ene",
        "Feb",
        "Mar",
        "Abr",
        "May",
        "Jun",
        "Jul",
        "Ago",
        "Sep",
        "Oct",
        "Nov",
        "Dic",
      ],
      today: "Hoy",
      clear: "Limpiar",
      weekHeader: "Sem",
      accept: "Aceptar",
      reject: "Rechazar",
      cancel: "Cancelar",
      choose: "Elegir",
      upload: "Subir",
      aria: {
        trueLabel: "Verdadero",
        falseLabel: "Falso",
        nullLabel: "No seleccionado",
      },
    },
    ripple: true,
  });

  // Servicios
  app.use(ConfirmationService);
  app.use(ToastService);
  app.use(DialogService);
}
